import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SectionRequest } from '../types/api';
import { useSectionsApi } from './use-sections-api';

const QK_GET_SECTION = (companyId: string, sectionId: string) =>
  ['company', companyId, 'section', sectionId] as const;

export function useSections(companyId: string | undefined, leafOnly: boolean | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');

  return useQuery({
    queryKey: ['company', companyId ?? '', 'sections', leafOnly ?? false],
    queryFn: () => sectionsApi.get(leafOnly),
    enabled: !!companyId,
  });
}

export function useSection(companyId: string | undefined, sectionId: string | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');

  return useQuery({
    queryKey: QK_GET_SECTION(companyId ?? '', sectionId ?? ''),
    queryFn: () => sectionsApi.section({ by: 'sparklayer', identifier: sectionId ?? '' }).get(),
    enabled: !!companyId && !!sectionId,
  });
}

export function useCreateSection(companyId: string | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SectionRequest) => sectionsApi.create(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId ?? '', 'sections'] });
    },
  });
}

export function useUpdateSection(companyId: string | undefined, sectionId: string | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SectionRequest) =>
      sectionsApi.section({ by: 'sparklayer', identifier: sectionId ?? '' }).update(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId ?? '', 'sections'] });
      void queryClient.invalidateQueries({
        queryKey: [QK_GET_SECTION(companyId ?? '', sectionId ?? '')],
      });
    },
  });
}

export function useUpdateSectionById(companyId: string | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      args:
        | SectionRequest
        | {
            payload: SectionRequest;
            options?: { invalidateQueries?: boolean };
          },
    ) => {
      const payload = 'payload' in args ? args.payload : args;
      return await sectionsApi
        .section({ by: 'sparklayer', identifier: payload.id ?? '' })
        .update(payload);
    },
    onSuccess: (_, args) => {
      const { id } = 'payload' in args ? args.payload : args;
      const invalidateQueries =
        'options' in args ? (args.options?.invalidateQueries ?? true) : true;
      if (invalidateQueries) {
        void queryClient.invalidateQueries({ queryKey: ['company', companyId ?? '', 'sections'] });
        void queryClient.invalidateQueries({
          queryKey: [QK_GET_SECTION(companyId ?? '', id ?? '')],
        });
      }
    },
  });
}

export function useDeleteSection(companyId: string | undefined, sectionId: string | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      sectionsApi.section({ by: 'sparklayer', identifier: sectionId ?? '' }).delete(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId ?? '', 'sections'] });
    },
  });
}

export function useDeleteSectionById(companyId: string | undefined) {
  const { sectionsApi } = useSectionsApi(companyId ?? '');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: string) =>
      sectionsApi.section({ by: 'sparklayer', identifier: sectionId }).delete(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['company', companyId ?? '', 'sections'] });
    },
  });
}
